import { z } from 'zod';
import { TipoPontuacao, StatusCategoriaTorneio } from '../../types/categoriaTorneio';
import { getValidTiebreakValues } from '../../utils/tiebreakOptions';

export const tipoGrupoChaveEnum = z.enum(['GRUPO', 'CHAVE', 'GRUPO-CHAVE']);
export const statusFaseEnum = z.enum([
    'AGENDADA',
    'EM_ANDAMENTO',
    'FINALIZADA',
    'CANCELADA'
]);
export const statusCategoriaEnum = z.nativeEnum(StatusCategoriaTorneio);

export type ModeloDisputa = z.infer<typeof modeloDisputaEnum>;

export const modeloDisputaEnum = z.enum([
  'ELIMINACAO_SIMPLES',
  'ELIMINACAO_DUPLA',
  'GRUPOS_ELIMINACAO',
  'GRUPO_UNICO',
  'LIGA',
  'SUICO',
  'REI_RAINHA',
  'SUPER_8'
]);

export const editCategorySchema = z.object({
  nome: z.string().min(3, 'Nome obrigatório'),
  descricao: z.string().optional(),
  genero: z.string(),
  numeroAtletas: z.number({ invalid_type_error: 'Número de atletas inválido' }).int('Número de atletas deve ser inteiro').positive('Número de atletas deve ser maior que 0'),
  precoInscricao: z.number({ invalid_type_error: 'Preço inválido' }).nonnegative('Preço não pode ser negativo'),
  regulamento: z.string().optional(),
  aplicarDescontoTorneio: z.boolean().optional(),
  // configFilaEspera: tri-estado (true = ativar, false = desativar, null = herda do torneio).
  // .nullable() é essencial — null é valor de domínio distinto de undefined.
  configFilaEspera: z.boolean().nullable().optional(),
  status: statusCategoriaEnum.optional(),
  vagas: z.number({ invalid_type_error: 'Número de vagas inválido' }).int('Número de vagas deve ser inteiro').positive('Número de vagas deve ser maior que 0').optional().or(z.nan().transform(() => undefined)),
  dataInicio: z.string().optional(),
  dataFim: z.string().optional(),
  dataLimPag: z.string().optional(),
  modeloDisputa: z.preprocess((val) => val === '' ? undefined : val, z.union([modeloDisputaEnum, z.string()]).optional()),
  idadeInicial: z.number({ invalid_type_error: 'Idade inicial inválida' }).int('Idade inicial deve ser inteira').nonnegative('Idade inicial não pode ser negativa').optional().or(z.nan().transform(() => undefined)),
  idadeFinal: z.number({ invalid_type_error: 'Idade final inválida' }).int('Idade final deve ser inteira').nonnegative('Idade final não pode ser negativa').optional().or(z.nan().transform(() => undefined)),
  idadeCombinada: z.number({ invalid_type_error: 'Idade combinada inválida' }).int('Idade combinada deve ser inteira').nonnegative('Idade combinada não pode ser negativa').optional().or(z.nan().transform(() => undefined)),
  quantidadeGrupos: z.number({ invalid_type_error: 'Quantidade de grupos inválida' }).int('Quantidade de grupos deve ser inteira').positive('Quantidade de grupos deve ser maior que 0').optional().or(z.nan().transform(() => undefined)),
  // .nonnegative() (não .positive()): 0 é valor de domínio legítimo para modelos
  // de grupo fixo (GRUPO_UNICO/REI_RAINHA/SUPER_8), que gravam classificadosPorGrupo=0.
  // A exigência de "> 0" vale só para GRUPOS_ELIMINACAO e é feita no superRefine abaixo.
  classificadosPorGrupo: z.number({ invalid_type_error: 'Classificados por grupo inválido' }).int('Classificados por grupo deve ser inteiro').nonnegative('Classificados por grupo não pode ser negativo').optional().or(z.nan().transform(() => undefined)),
  tamanhoGrupo: z.number({ invalid_type_error: 'Tamanho de grupo inválido' }).int('Tamanho de grupo deve ser inteiro').positive('Tamanho de grupo deve ser maior que 0').optional().or(z.nan().transform(() => undefined)),
  tipoPontuacao: z.preprocess((val) => val === '' ? undefined : val, z.nativeEnum(TipoPontuacao).optional()),
  numeroSets: z.number({ invalid_type_error: 'Número de sets inválido' }).int('Número de sets deve ser inteiro').min(1, 'Número de sets deve ser entre 1 e 5').max(5, 'Número de sets deve ser entre 1 e 5').optional().or(z.nan().transform(() => undefined)),
  gamesPorSets: z.number({ invalid_type_error: 'Games por set inválido' }).int('Games por set deve ser inteiro').min(1, 'Games por set deve ser entre 1 e 11').max(11, 'Games por set deve ser entre 1 e 11').optional().or(z.nan().transform(() => undefined)),
  tiebreaks: z.number({ invalid_type_error: 'Tiebreak inválido' }).int('Tiebreak deve ser inteiro').min(1, 'Tiebreak deve ser entre 1 e 11').max(11, 'Tiebreak deve ser entre 1 e 11').optional().or(z.nan().transform(() => undefined)),
  // Vínculo opcional com a CategoriaLiga escolhida como template (AC-2). Aditivo —
  // sem seleção de template, permanece undefined e o fluxo atual não regride (AC-5).
  categoriaLigaId: z.string().optional(),
}).superRefine((data, ctx) => {
  // Checagem cruzada: o tiebreak precisa ser uma das opções válidas para o
  // games por set escolhido (gamesPorSets-1, gamesPorSets ou "sem tiebreak").
  // Bloqueia salvar combinações inconsistentes como gamesPorSets=6, tiebreaks=1.
  if (
    data.tiebreaks != null &&
    data.gamesPorSets != null &&
    !getValidTiebreakValues(data.gamesPorSets).includes(data.tiebreaks)
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['tiebreaks'],
      message: `Tiebreak inválido para ${data.gamesPorSets} games por set.`,
    });
  }

  // GRUPOS_ELIMINACAO exige ao menos 1 classificado por grupo. Os demais modelos
  // (grupo fixo) gravam 0 legitimamente — daí a checagem ser condicional ao modelo.
  if (
    data.modeloDisputa === 'GRUPOS_ELIMINACAO' &&
    (data.classificadosPorGrupo == null || data.classificadosPorGrupo < 1)
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['classificadosPorGrupo'],
      message: 'Classificados por grupo deve ser maior que 0.',
    });
  }
});

export type EditCategoryFormValues = z.infer<typeof editCategorySchema>;

export const toCategoriaTorneioInput = (
  values: EditCategoryFormValues,
  torneioId: string
) => ({
  nome: values.nome,
  torneioId,
  status: values.status || StatusCategoriaTorneio.AGENDADA,
  descricao: values.descricao?.trim() || undefined,
  genero: values.genero as 'MASCULINO' | 'FEMININO' | 'MISTO',
  numeroAtletas: values.numeroAtletas,
  precoInscricao: values.precoInscricao,
  aplicarDescontoTorneio: values.aplicarDescontoTorneio,
  // Preserva null (distinto de undefined): null = "herda do torneio".
  configFilaEspera: values.configFilaEspera === undefined ? null : values.configFilaEspera,
  vagas: values.vagas,
  dataInicio: values.dataInicio?.trim() || undefined,
  dataFim: values.dataFim?.trim() || undefined,
  dataLimPag: values.dataLimPag?.trim() || undefined,
  modeloDisputa: values.modeloDisputa,
  idadeInicial: values.idadeInicial,
  idadeFinal: values.idadeFinal,
  idadeCombinada: values.idadeCombinada,
  quantidadeGrupos: values.quantidadeGrupos,
  classificadosPorGrupo: values.classificadosPorGrupo,
  tamanhoGrupo: values.tamanhoGrupo,
  tipoPontuacao: values.tipoPontuacao,
  numeroSets: values.numeroSets,
  gamesPorSets: values.gamesPorSets,
  tiebreaks: values.tiebreaks,
  categoriaLigaId: values.categoriaLigaId,
});

export const phaseSchema = z.object({
  id: z.string().optional(),
  categoriaTorneioId: z.string(),
  nome: z.string().min(1, 'Nome obrigatório'),
  dataInicio: z.date(),
  dataFim: z.date(),
  status: statusFaseEnum,
  ordem: z.number().int().nonnegative(),
  tipoGrupoChave: z.string(),
}).refine((data) => data.dataInicio <= data.dataFim, {
  message: 'Data inicial deve ser anterior ou igual à final',
  path: ['dataFim'],
});

export type PhaseFormValues = z.infer<typeof phaseSchema>;
